// src/pages/Admin/AuditLog.tsx
/**
 * Admin → Audit Log
 * -----------------------------------------------------------------------------
 * Fixes build errors by:
 *  - Removing references to non-existent `TBL.auditLog` key
 *  - Reading/writing audit entries through "@/lib/audit" helpers
 *  - Using DataTable with { key, label } columns (no generics)
 *
 * Features (lightweight, compile-safe):
 *  - Search by action/role/actor/details
 *  - Date range filter
 *  - CSV export via DataTable toolbar
 *  - Clear all logs
 */

import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import DataTable, { Col } from "@/components/DataTable";
import { get, set } from "@/lib/storage";
import { readAudit, clearAudit, AuditEntry } from "@/lib/audit";
import { Trash2, RefreshCw } from "lucide-react";
import { useNotifications } from "@/lib/notifications";

// Local storage keys for view prefs only (NOT the audit store itself)
const TBL = {
  audit: "tbl:audit-log:v1", // used only for column chooser persistence
} as const;

type Filters = {
  q: string;
  start?: string; // yyyy-mm-dd
  end?: string;   // yyyy-mm-dd
  density: "cozy" | "compact";
};

const DEFAULT_FILTERS: Filters = {
  q: "",
  start: "",
  end: "",
  density: get<Filters["density"]>("ui:table:density", "cozy"),
};

function withinRange(ts: string, start?: string, end?: string) {
  const t = new Date(ts).getTime();
  if (start) {
    const s = new Date(start).getTime();
    if (t < s) return false;
  }
  if (end) {
    const e = new Date(end).getTime();
    if (t > e) return false;
  }
  return true;
}

export default function AuditLog() {
  const { push } = useNotifications();
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [rows, setRows] = useState<AuditEntry[]>([]);

  // Load on mount
  useEffect(() => {
    setRows(readAudit());
  }, []);

  // Persist density as a shared UI pref
  useEffect(() => {
    set("ui:table:density", filters.density);
  }, [filters.density]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.start || filters.end) {
        if (!withinRange(r.ts, filters.start || undefined, filters.end || undefined)) {
          return false;
        }
      }
      if (!q) return true;
      const hay = [
        r.action,
        r.role,
        r.actor,
        r.ts,
        // stringify remaining metadata shallowly
        JSON.stringify(r),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filters]);

  const cols: Col[] = useMemo(
    () => [
      {
        key: "ts",
        label: "When",
        width: 180,
        className: "whitespace-nowrap",
        render: (r: AuditEntry) =>
          new Date(r.ts).toLocaleString([], { hour: "2-digit", minute: "2-digit", year: "numeric", month: "short", day: "2-digit" }),
      },
      {
        key: "action",
        label: "Action",
        width: 240,
        render: (r: AuditEntry) => <span className="font-medium">{r.action}</span>,
      },
      {
        key: "role",
        label: "Role",
        width: 120,
        render: (r: AuditEntry) => r.role || "—",
      },
      {
        key: "actor",
        label: "Actor",
        width: 140,
        render: (r: AuditEntry) => r.actor || "—",
      },
      {
        key: "details",
        label: "Details",
        render: (r: AuditEntry) => {
          // show everything except ts/id/action/role/actor
          const { ts, id, action, role, actor, ...rest } = r;
          const text = Object.keys(rest).length ? JSON.stringify(rest) : "—";
          return <span className="text-xs break-all">{text}</span>;
        },
      },
    ],
    []
  );

  const refresh = () => {
    setRows(readAudit());
    push({ title: "Audit log refreshed" });
  };

  const onClear = () => {
    if (!window.confirm("Clear ALL audit entries? This cannot be undone.")) return;
    clearAudit();
    setRows([]);
    push({ title: "Audit log cleared" });
  };

  return (
    <AppShell title="Audit Log">
      {/* Controls */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <div className="font-medium">Filters</div>
          <div className="flex items-center gap-2">
            <button
              className="btn btn-secondary"
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  density: f.density === "cozy" ? "compact" : "cozy",
                }))
              }
              title="Toggle density"
            >
              {filters.density === "cozy" ? "Cozy" : "Compact"}
            </button>
            <button className="btn btn-secondary" onClick={refresh} title="Refresh">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button className="btn btn-danger" onClick={onClear} title="Clear all">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input
              className="input md:col-span-2"
              placeholder="Search actions, role, actor, details…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
            <div className="flex gap-2">
              <input
                type="date"
                className="input"
                value={filters.start}
                onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
                aria-label="From date"
              />
              <input
                type="date"
                className="input"
                value={filters.end}
                onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
                aria-label="To date"
              />
            </div>
          </div>
          <div className="mt-2 text-sm opacity-70">
            Showing <span className="font-medium">{filtered.length}</span> of{" "}
            <span className="font-medium">{rows.length}</span> events
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className={`table-shell ${
          filters.density === "compact" ? "density-compact" : ""
        } table-sticky table-zebra`}
      >
        <DataTable
          columns={cols}
          rows={filtered}
          filename="audit-log"
          storageKey={TBL.audit}
          ariaLabel="Audit events"
        />
      </div>
    </AppShell>
  );
}
